import React, { useState, useEffect, useCallback } from "react";
import {
  Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TablePagination, Box, Chip, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, Grid, Divider,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import RefreshIcon from "@mui/icons-material/Refresh";
import { instance } from "../../axios";
import dayjs from "dayjs";
import { fmtDt } from "../../utils/date";

const TABLES = ["Visit", "Patient", "Company"];
const OPERATIONS = ["CREATE", "UPDATE", "DELETE"];

const operationColor = { CREATE: "success", UPDATE: "primary", DELETE: "error" };

const AuditList = () => {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(0);
  const [rowsPerPage]         = useState(20);
  const [loading, setLoading] = useState(false);

  // Filtres
  const [filterTable, setFilterTable]         = useState("");
  const [filterOperation, setFilterOperation] = useState("");
  const [filterUser, setFilterUser]           = useState("");
  const [filterFrom, setFilterFrom]           = useState("");
  const [filterTo, setFilterTo]               = useState("");
  const [users, setUsers]                     = useState([]);

  // Détail
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    instance.get("/audit/users").then(res => setUsers(res.data || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      limit:  rowsPerPage,
      offset: page * rowsPerPage,
    });
    if (filterTable)     params.set("table",     filterTable);
    if (filterOperation) params.set("operation", filterOperation);
    if (filterUser)      params.set("userId",    filterUser);
    if (filterFrom)      params.set("from",      filterFrom);
    if (filterTo)        params.set("to",        filterTo);

    instance.get(`/audit?${params}`)
      .then(res => { setRows(res.data.data || []); setTotal(res.data.total || 0); })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, filterTable, filterOperation, filterUser, filterFrom, filterTo]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 3, backgroundColor: "white" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Audit</Typography>
          <Tooltip title="Actualizar">
            <IconButton onClick={load} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Filtros */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Tabla</InputLabel>
            <Select value={filterTable} label="Tabla" onChange={handleFilterChange(setFilterTable)}>
              <MenuItem value="">Todas</MenuItem>
              {TABLES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Operación</InputLabel>
            <Select value={filterOperation} label="Operación" onChange={handleFilterChange(setFilterOperation)}>
              <MenuItem value="">Todas</MenuItem>
              {OPERATIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Usuario</InputLabel>
            <Select value={filterUser} label="Usuario" onChange={handleFilterChange(setFilterUser)}>
              <MenuItem value="">Todos</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={String(u.id)}>{u.displayName}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Desde"
            type="date"
            value={filterFrom}
            onChange={handleFilterChange(setFilterFrom)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <TextField
            size="small"
            label="Hasta"
            type="date"
            value={filterTo}
            onChange={handleFilterChange(setFilterTo)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
        </Box>

        {/* Tabla */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tabla</TableCell>
                <TableCell>Operación</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell align="center">Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">Cargando...</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">Sin resultados</TableCell>
                </TableRow>
              ) : rows.map(row => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {fmtDt(row.timestamp)}
                  </TableCell>
                  <TableCell>{row.tableName}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.operation}
                      color={operationColor[row.operation] || "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{row.userDisplayName}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver valores">
                      <IconButton size="small" onClick={() => setDetail(row)}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20]}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
        />
      </Paper>

      {/* Diálogo detalle */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">{detail?.tableName} #{detail?.id}</Typography>
            <Chip
              label={detail?.operation}
              color={operationColor[detail?.operation] || "default"}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {detail?.oldValues && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Antes</Typography>
                <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all", background: "#f5f5f5", padding: 8, borderRadius: 4, margin: 0 }}>
                  {JSON.stringify(detail.oldValues, null, 2)}
                </pre>
              </Box>
            )}
            {detail?.newValues && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Después</Typography>
                <pre style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all", background: "#f5f5f5", padding: 8, borderRadius: 4, margin: 0 }}>
                  {JSON.stringify(detail.newValues, null, 2)}
                </pre>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default AuditList;
